import { Delta, getLines } from '@typewriter/editor';

class Paper {
    constructor(types) {
        this.subscribers = [];
        this.blocks = new Types(this, types && types.blocks);
        this.marks = new Types(this, types && types.marks);
        this.embeds = new Types(this, types && types.embeds);
    }
    subscribe(run) {
        this.subscribers.push(run);
        run(this);
        return () => {
            const index = this.subscribers.indexOf(run);
            if (index !== -1)
                this.subscribers.splice(index, 1);
        };
    }
    updated() {
        this.subscribers.forEach(run => run(this));
    }
}
class Types {
    constructor(paper, types) {
        this.paper = paper;
        this.selector = '';
        this.types = {};
        this.list = [];
        this.priorities = {};
        if (types)
            types.forEach(type => this.add(type, undefined, true));
    }
    add(type, priority, skipUpdate = false) {
        if (!type.name || !type.selector) {
            throw new Error('Paper Type definitions must include a name and selector');
        }
        if (this.types[type.name])
            this.remove(type.name);
        this.selector += (this.selector ? ', ' : '') + type.selector;
        this.types[type.name] = type;
        if (priority === undefined) {
            this.priorities[type.name] = this.list.length;
            this.list.push(type);
        }
        else {
            this.list.splice(priority, 0, type);
            this.list.forEach(({ name }, i) => this.priorities[name] = i);
        }
        if (!skipUpdate)
            this.paper.updated();
    }
    remove(name) {
        if (!this.types[name])
            return;
        delete this.types[name];
        this.list = this.list.filter(type => type.name !== name);
        this.list.forEach(({ name }, i) => this.priorities[name] = i);
        this.selector = this.list.map(type => type.selector).join(', ');
        this.paper.updated();
    }
    clear() {
        this.selector = '';
        this.types = {};
        this.list = [];
        this.priorities = {};
        this.paper.updated();
    }
    get(name) {
        return this.types[name];
    }
    priority(name) {
        return this.priorities[name];
    }
    getDefault() {
        return this.list[0];
    }
    matches(node) {
        if (!node.nodeType)
            throw new Error('Cannot match against ' + node);
        if (node.nodeType === Node.ELEMENT_NODE) {
            return this.selector ? node.matches(this.selector) : false;
        }
    }
    findByNode(node, fallbackToDefault = false) {
        if (node.nodeType !== Node.ELEMENT_NODE)
            return;
        let i = this.list.length;
        while (i--) {
            let type = this.list[i];
            if (node.matches(type.selector))
                return type;
        }
        if (fallbackToDefault)
            return this.getDefault();
    }
    findByAttributes(attributes, fallbackToDefault = false) {
        const keys = attributes && Object.keys(attributes);
        let type;
        keys && keys.every(name => !(type = this.get(name)));
        return type || (fallbackToDefault ? this.getDefault() : undefined);
    }
}

function isBRPlaceholder(paper, node) {
    if (node.nodeName !== 'BR')
        return false;
    const { blocks } = paper;
    let next = node.nextSibling;
    while (next && next.nodeValue === '')
        next = next.nextSibling;
    if (next) {
        return next.nodeType === Node.ELEMENT_NODE && blocks.matches(next);
    }
    return blocks.matches(node.parentNode);
}

const SKIP_ELEMENTS = { STYLE: true, SCRIPT: true, LINK: true, META: true, TITLE: true, };
const BLOCK_ELEMENTS = 'address, article, aside, blockquote, canvas, dd, div, dl, dt, fieldset, figcaption, figure, footer, form, header, hr, li, main, nav, noscript, ol, output, p, pre, section, table, tfoot, ul, video';
const defaultOptions = { notInDom: false, };
function deltaFromDom(root, paper, options = {}) {
    if (!root.ownerDocument)
        return new Delta();
    const inDom = root.ownerDocument && root.ownerDocument.contains(root);
    const { blocks, embeds } = paper;
    if (!options)
        options = defaultOptions;
    var walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
        acceptNode: node => {
            if (SKIP_ELEMENTS[node.nodeName]) {
                return NodeFilter.FILTER_REJECT;
            }
            else if (node.nodeType === Node.TEXT_NODE && node.nodeValue === '') {
                return NodeFilter.FILTER_REJECT;
            }
            else if (node.nodeType === Node.TEXT_NODE || options.notInDom || inDom) {
                return NodeFilter.FILTER_ACCEPT;
            }
            else {
                return NodeFilter.FILTER_REJECT;
            }
        }
    });
    const delta = new Delta();
    let currentBlock, firstBlockSeen = false, unknownBlock = false, empty = true, node;
    let lastNode = false;
    if (options.startNode) {
        walker.currentNode = options.startNode;
        walker.previousNode();
        if (options.offset)
            delta.retain(options.offset, undefined);
    }
    else {
        walker.currentNode = root;
    }
    while ((node = walker.nextNode())) {
        if (node === options.endNode)
            lastNode = true;
        else if (lastNode)
            break;
        if (isBRPlaceholder(paper, node)) {
            empty = false;
        }
        else if (node.nodeType === Node.TEXT_NODE) {
            let parent = node.parentNode;
            if (node.nodeValue == null || node.nodeValue.replace(/\n+/g, '') === '')
                continue;
            const text = node.nodeValue.replace(/\s+/g, ' ').replace(/\xA0/g, ' ');
            if (!text || (text === ' ' && parent.classList.contains('EOP')))
                continue;
            const attributes = gatherMarks(parent, root, paper);
            empty = false;
            delta.insert(text, attributes);
        }
        else if (embeds.matches(node)) {
            const embed = embeds.findByNode(node);
            if (embed) {
                const attributes = gatherMarks(node.parentNode, root, paper);
                if (embed.fromDom !== false) {
                    delta.insert(embed.fromDom ? embed.fromDom(node, paper) : { [embed.name]: true }, attributes);
                }
            }
        }
        else if (blocks.matches(node) || (node.nodeType === Node.ELEMENT_NODE && node.matches(BLOCK_ELEMENTS))) {
            unknownBlock = !blocks.matches(node);
            if (unknownBlock) {
                let parent = node.parentNode;
                while (parent && !blocks.matches(parent) && parent !== root) {
                    parent = parent.parentNode;
                }
                if (parent && parent !== root) {
                    continue;
                }
            }
            const block = blocks.findByNode(node, true);
            if (block === blocks.getDefault() && (!node.parentNode || blocks.matches(node.parentNode))) {
                continue;
            }
            if (firstBlockSeen) {
                if (!currentBlock.unknownBlock || !empty) {
                    delta.insert('\n', currentBlock.unknownBlock ? {} : currentBlock);
                    empty = true;
                }
            }
            else {
                firstBlockSeen = true;
            }
            if (unknownBlock) {
                currentBlock = { unknownBlock };
            }
            else if (block && block !== blocks.getDefault()) {
                currentBlock = block.fromDom ? block.fromDom(node, paper) : { [block.name]: true };
            }
            else {
                currentBlock = {};
            }
        }
    }
    if (!unknownBlock || !empty) {
        delta.insert('\n', currentBlock);
    }
    return delta;
}
function gatherMarks(parent, root, paper) {
    const { blocks, marks } = paper;
    const attributes = {};
    while (parent && !blocks.matches(parent) && parent !== root) {
        if (marks.matches(parent)) {
            const mark = marks.findByNode(parent);
            if (mark && mark.fromDom !== false) {
                attributes[mark.name] = mark.fromDom ? mark.fromDom(parent, paper) : true;
            }
        }
        else if (parent.hasAttribute('style')) {
            marks.list.forEach(mark => {
                if (mark.styleSelector && parent.matches(mark.styleSelector)) {
                    attributes[mark.name] = mark.fromDom ? mark.fromDom(parent, paper) : true;
                }
            });
        }
        parent = parent.parentNode;
    }
    return attributes;
}

function deltaFromHTML(paper, html) {
    const template = document.createElement('template');
    template.innerHTML = '<div>' + html + '</div>';
    return deltaFromDom(template.content.firstChild, paper, { notInDom: true });
}

const modifierKeys = {
    Control: true,
    Meta: true,
    Shift: true,
    Alt: true
};
const isMac = navigator.userAgent.indexOf('Macintosh') !== -1;
function shortcutFromEvent(event) {
    const shortcutArray = [];
    let key = event.key;
    if (!key)
        return '';
    if (key === ' ')
        key = 'Space';
    if (event.metaKey)
        shortcutArray.push('Cmd');
    if (event.ctrlKey)
        shortcutArray.push('Ctrl');
    if (event.altKey)
        shortcutArray.push('Alt');
    if (event.shiftKey)
        shortcutArray.push('Shift');
    if (!modifierKeys[key]) {
        if (isMac && event.altKey && event.code && event.code.startsWith('Key')) {
            key = event.code.replace('Key', '');
        }
        if (key.length === 1)
            key = key.toUpperCase();
        shortcutArray.push(key);
    }
    return shortcutArray.join('+');
}

function decorate(root, contents) {
    const decorators = new Decorators(contents);
    root.dispatchEvent(new CustomEvent('decorate', { detail: decorators }));
    const change = decorators.getChange();
    if (change.ops.length) {
        change.forEach(op => {
            if (op.delete || (op.retain && op.attributes && !op.attributes.decorator) || (op.insert && !op.insert.decorator)) {
                throw new Error('Decorators may not insert text or delete contents.');
            }
        });
        contents = contents.compose(change);
    }
    return contents;
}
class Decorators {
    constructor(contents) {
        this.contents = contents;
        this.change;
        this.delta = new Delta();
        this.position = 0;
    }
    mark(from, to, attributes) {
        [from, to] = this.updatePosition(from, to);
        const length = to - from;
        this.delta.retain(length, { decorator: attributes });
        this.position += length;
    }
    embed(at, attributes) {
        this.updatePosition(at);
        this.delta.insert({ decorator: attributes });
    }
    line(at, attributes) {
        const line = getLines(this.contents, at, at)[0];
        if (!line)
            return;
        this.updatePosition(line.end - 1);
        this.delta.retain(1, { decorator: attributes });
        this.position += 1;
    }
    getChange() {
        return this.change ? this.change.compose(this.delta) : this.delta;
    }
    updatePosition(from, to) {
        if (from == null || isNaN(from) || (to != null && isNaN(to))) {
            throw new Error('Decorators must have valid indexes');
        }
        if (this.change) {
            from = this.change.transformPosition(from);
            if (to != null)
                to = this.change.transformPosition(to);
        }
        if (this.position <= from) {
            this.delta.retain(from - this.position);
            this.position = from;
        }
        else if (this.position) {
            this.change = this.getChange();
            from = this.change.transformPosition(from);
            if (to != null)
                to = this.change.transformPosition(to);
            this.delta = new Delta();
            this.delta.retain(from);
            this.position = from;
        }
        return to != null ? [from, to] : from;
    }
}

function getSelection(root, paper, range) {
    if (!root.ownerDocument)
        return null;
    const selection = !range ? root.ownerDocument.getSelection() : {
        anchorNode: range.startContainer, anchorOffset: range.startOffset,
        focusNode: range.endContainer, focusOffset: range.endOffset,
        isCollapsed: range.collapsed,
    };
    if (selection == null || selection.anchorNode == null || selection.focusNode == null || !root.contains(selection.anchorNode)) {
        return null;
    }
    else {
        const anchorIndex = getNodeAndOffsetIndex(root, paper, selection.anchorNode, selection.anchorOffset);
        const isCollapsed = selection.anchorNode === selection.focusNode && selection.anchorOffset === selection.focusOffset;
        let focusIndex = isCollapsed ?
            anchorIndex :
            getNodeAndOffsetIndex(root, paper, selection.focusNode, selection.focusOffset);
        return [anchorIndex, focusIndex];
    }
}
function setSelection(root, paper, range) {
    if (!root.ownerDocument)
        return;
    const selection = root.ownerDocument.getSelection();
    if (!selection)
        return;
    const hasFocus = selection.anchorNode && root.contains(selection.anchorNode);
    if (range == null) {
        if (hasFocus) {
            root.blur();
            selection.removeAllRanges();
        }
    }
    else {
        const [anchorNode, anchorOffset, focusNode, focusOffset] = getNodesForRange(root, paper, range);
        const type = range[0] === range[1] ? 'Caret' : 'Range';
        if (anchorNode && focusNode) {
            if (selection.anchorNode !== anchorNode || selection.anchorOffset !== anchorOffset ||
                selection.focusNode !== focusNode || selection.focusOffset !== focusOffset || selection.type !== type) {
                selection.setBaseAndExtent(anchorNode, anchorOffset, focusNode, focusOffset);
            }
        }
        if (!hasFocus)
            root.focus();
    }
}
function getBounds(root, paper, range) {
    return getBoudingBrowserRange(root, paper, range).getBoundingClientRect();
}
function getAllBounds(root, paper, range) {
    return getBoudingBrowserRange(root, paper, range).getClientRects();
}
function getBoudingBrowserRange(root, paper, range) {
    const browserRange = getBrowserRange(root, paper, range);
    if (browserRange.endContainer.nodeType === Node.ELEMENT_NODE) {
        try {
            browserRange.setEnd(browserRange.endContainer, browserRange.endOffset + 1);
        }
        catch (e) { }
    }
    return browserRange;
}
function getBrowserRange(root, paper, range) {
    if (range[0] > range[1])
        range = [range[1], range[0]];
    const [anchorNode, anchorOffset, focusNode, focusOffset] = getNodesForRange(root, paper, range);
    const browserRange = document.createRange();
    if (anchorNode && focusNode) {
        browserRange.setStart(anchorNode, anchorOffset);
        browserRange.setEnd(focusNode, focusOffset);
    }
    return browserRange;
}
function getNodesForRange(root, paper, range) {
    if (range == null) {
        return [null, 0, null, 0];
    }
    else {
        const [anchorNode, anchorOffset] = getNodeAndOffset(root, paper, range[0]);
        const [focusNode, focusOffset] = range[0] === range[1] ?
            [anchorNode, anchorOffset] : getNodeAndOffset(root, paper, range[1]);
        return [anchorNode, anchorOffset, focusNode, focusOffset];
    }
}
function getNodeAndOffset(root, paper, index) {
    if (!root.ownerDocument)
        return [null, 0];
    const inDom = root.ownerDocument.contains(root);
    const { blocks, embeds } = paper;
    const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
        acceptNode: inDom ? acceptNodeInDom : acceptNode
    });
    let count = 0, node, firstBlockSeen = false;
    walker.currentNode = root;
    while ((node = walker.nextNode())) {
        if (node.nodeType === Node.TEXT_NODE) {
            const size = node.nodeValue ? node.nodeValue.length : 0;
            if (index <= count + size)
                return [node, index - count];
            count += size;
        }
        else if (embeds.matches(node) && !isBRPlaceholder(paper, node)) {
            const embed = embeds.findByNode(node);
            if (!embed || embed.fromDom === false) {
                continue;
            }
            count += 1;
            const next = nextNonEmptyTextSibling(node);
            if (count === index && (!next || next.nodeType !== Node.TEXT_NODE)) {
                const children = node.parentNode ? Array.from(node.parentNode.childNodes) : [];
                return [node.parentNode, children.indexOf(node) + 1];
            }
        }
        else if (blocks.matches(node)) {
            if (firstBlockSeen)
                count += 1;
            else
                firstBlockSeen = true;
            if (count === index) {
                const first = firstNonEmptyTextChild(node);
                if (!first)
                    return [node, 0];
                else if (first.nodeType !== Node.TEXT_NODE) {
                    const children = Array.from(node.childNodes);
                    return [node, children.indexOf(first)];
                }
            }
        }
    }
    return [null, 0];
}
function getNodeAndOffsetIndex(root, paper, node, offset) {
    if (node.nodeType === Node.ELEMENT_NODE) {
        if (offset) {
            node = node.childNodes[offset - 1];
            while (node.lastChild)
                node = node.lastChild;
            offset = node.nodeType === Node.ELEMENT_NODE ? 0 : (node.nodeValue ? node.nodeValue.length : 0);
        }
        else {
            offset = 1;
        }
    }
    return getNodeIndex(root, paper, node) + offset;
}
function getNodeIndex(root, paper, startNode) {
    if (!root.ownerDocument)
        return -1;
    const inDom = root.ownerDocument.contains(root);
    const { blocks, embeds } = paper;
    const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
        acceptNode: inDom ? acceptNodeInDom : acceptNode
    });
    walker.currentNode = startNode;
    let node;
    let index = -1;
    while ((node = walker.previousNode())) {
        if (node === root)
            continue;
        else if (node.nodeType === Node.TEXT_NODE)
            index += node.nodeValue ? node.nodeValue.length : 0;
        else if (node.className.indexOf('decorator') !== -1)
            ;
        else if (embeds.matches(node) && !isBRPlaceholder(paper, node))
            index++;
        else if (blocks.matches(node))
            index++;
    }
    return index;
}
function acceptNode(node) {
    if (node.nodeType === Node.TEXT_NODE) {
        return node.nodeValue && node.nodeValue.length ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    }
    return NodeFilter.FILTER_REJECT;
}
function acceptNodeInDom(node) {
    if (node.nodeType === Node.TEXT_NODE) {
        return node.nodeValue && node.nodeValue.length ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    }
    return NodeFilter.FILTER_ACCEPT;
}
function firstNonEmptyTextChild(parent) {
    let node = parent.firstChild;
    if (!node)
        return null;
    while (node && node.nodeValue === '') {
        node = node.nextSibling;
    }
    return node;
}
function nextNonEmptyTextSibling(node) {
    let nextSibling = node.nextSibling;
    while (nextSibling && nextSibling.nodeValue === '') {
        nextSibling = nextSibling.nextSibling;
    }
    return nextSibling;
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
}

function getDefaultPaper() {
    return new Paper({
        blocks: [paragraph, header, list, blockquote, codeblock, hr],
        marks: [link, decorator, bold, italic, code],
        embeds: [image, br, decorator],
    });
}
const paragraph = {
    name: 'paragraph',
    selector: 'p',
};
const header = {
    name: 'header',
    selector: 'h1, h2, h3, h4, h5, h6',
    defaultFollows: true,
    fromDom: (node) => ({ header: parseInt(node.nodeName.replace('H', '')) }),
};
const list = {
    name: 'list',
    selector: 'ul > li, ol > li',
    indentable: true,
    fromDom(node) {
        let indent = -1, parent = node.parentNode;
        const list = parent && parent.nodeName === 'OL' ? 'ordered' : 'bullet';
        while (parent) {
            if (/^UL|OL$/.test(parent.nodeName))
                indent++;
            else if (parent.nodeName !== 'LI')
                break;
            parent = parent.parentNode;
        }
        const attr = { list };
        if (indent)
            attr.indent = indent;
        return attr;
    },
    getNextLineAttributes(attrs) {
        const rest = __rest(attrs, ["start"]);
        return rest;
    }
};
const blockquote = {
    name: 'blockquote',
    selector: 'blockquote p',
    fromDom(node) {
        const { className } = node.parentNode;
        const match = className.match(/quote-(\S+)/);
        const blockquote = match && match[1] !== 'true' && match[1] || true;
        return { blockquote };
    }
};
const codeblock = {
    name: 'code-block',
    selector: 'pre code, pre',
};
const hr = {
    name: 'hr',
    selector: 'hr',
};
const bold = {
    name: 'bold',
    selector: 'strong, b',
    styleSelector: '[style*="bold"]',
};
const italic = {
    name: 'italic',
    selector: 'em, i',
    styleSelector: '[style*="italic"]',
};
const code = {
    name: 'code',
    selector: 'code',
};
const link = {
    name: 'link',
    selector: 'a[href]',
    fromDom: (node) => node.href
};
const image = {
    name: 'image',
    selector: 'img',
    fromDom: (node) => {
        const image = {};
        ['src', 'alt', 'width', 'height'].forEach(name => {
            if (!node.hasAttribute(name))
                return;
            const value = node.getAttribute(name);
            if (name === 'src')
                name = 'image';
            image[name] = value;
        });
        return image;
    },
};
const br = {
    name: 'br',
    selector: 'br',
};
const decorator = {
    name: 'decorator',
    selector: 'span.decorator',
    fromDom: false,
};

export { Decorators, Paper, blockquote, bold, br, code, codeblock, decorate, decorator, deltaFromDom, deltaFromHTML, getAllBounds, getBoudingBrowserRange, getBounds, getBrowserRange, getDefaultPaper, getNodeAndOffset, getNodeAndOffsetIndex, getNodeIndex, getNodesForRange, getSelection, header, hr, image, isBRPlaceholder, italic, link, list, paragraph, setSelection, shortcutFromEvent };
//# sourceMappingURL=index.esm.js.map
